import { defineStore } from "pinia";
import { ref } from "vue";
import requests from "@/services/requests";

export const useOfertasStore = defineStore("ofertas", () => {
  const tratamiento = ref(null);
  const indice = ref(null);
  const caracteristicas = ref(null);
  const loadingInfo = ref(false);

  // Promise-cache: estos endpoints devuelven datos estáticos por sesión/clave,
  // pero el flujo de PDFs los invoca 4× por pedido sin dedup. Guardamos la
  // promesa (no el valor) para que llamadas concurrentes con los mismos args
  // compartan la request en vuelo.
  let _loadInfoPromise = null;
  const _caracteristicasLentePromises = new Map();

  function _invalidateLoadInfoCache() { _loadInfoPromise = null; }
  function _invalidateCaracteristicasLenteCache() { _caracteristicasLentePromises.clear(); }

  async function LoadInfo() {
    if (_loadInfoPromise) return _loadInfoPromise;
    loadingInfo.value = true;
    _loadInfoPromise = (async () => {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      const data = await requests.ofertas.loadInfo(auth.username, auth.token);
      if (!data || data.status !== 200) return false;
      return data.data;
    })();
    // Si la promesa falla, no la dejamos pegada en la cache: el siguiente
    // intento debería poder reintentar.
    _loadInfoPromise
      .catch(() => { _loadInfoPromise = null; })
      .finally(() => { loadingInfo.value = false; });
    return _loadInfoPromise;
  }

  async function SaveInfo({ datos }) {
    try {
      const { useAuthStore } = await import("./auth");
      const auth = useAuthStore();
      if (!datos?.VALOR_PANTALLA) {
        return false;
      }
      const data = await requests.ofertas.saveInfo(datos.VALOR_PANTALLA, auth.username, auth.token);
      if (!data || data.status !== 200) {
        return false;
      }
      // VALOR_PANTALLA acaba de cambiar; invalidamos la cache para que el
      // próximo LoadInfo() traiga el valor fresco.
      _invalidateLoadInfoCache();
      return true;
    } catch (error) {
      return false;
    }
  }

  async function GetFotocromaticos({ org, cliente }) {
    const { useAuthStore } = await import("./auth");
    const auth = useAuthStore();

    const data = await requests.ofertas.getFotocromaticos(
      org,
      cliente,
      auth.token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetColoraciones({ payload }) {
    const { useAuthStore } = await import("./auth");
    const data = await requests.ofertas.get(
      "GetColoraciones",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetColor({ payload }) {
    const { useAuthStore } = await import("./auth");
    const data = await requests.ofertas.get(
      "GetColor",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetFocoyMaterial({ payload }) {
    const data = await requests.ofertas.get(
      "GetFocoMaterial",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetIndices({ payload }) {
    const data = await requests.ofertas.get(
      "GetIndices",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetTratamientos({ payload }) {
    const data = await requests.ofertas.get(
      "GetTratamientos",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetTonos({ payload }) {
    const data = await requests.ofertas.get(
      "GetTonos",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetLentes({ payload }) {
    const data = await requests.ofertas.get(
      "GetLentes",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetLentesAll(vkorg = null) {
    const { useAuthStore } = await import("./auth");
    const { useIndoStore } = await import("./indo");
    const auth = useAuthStore();
    const indoStore = useIndoStore();
    let lentes = [];
    if (vkorg) {
      const area = indoStore.profile.AreaVenta.listaAreasCliente.find((a) => a.ORG_VENTA === vkorg);
      if (!area) return [];
      const payload = {
        vkorg: area.ORG_VENTA,
        cliente: indoStore.profile.AreaVenta.CLIENTE,
        canal: area.CANAL_DIST,
      };
      const data = await requests.ofertas.get("GetLentesAll", payload, auth.token);
      if (data?.status !== 200) return false;
      lentes = data.data;
    } else {
      for (const area of indoStore.profile.AreaVenta.listaAreasCliente) {
        const payload = {
          vkorg: area.ORG_VENTA,
          cliente: indoStore.profile.AreaVenta.CLIENTE,
          canal: area.CANAL_DIST,
        };
        const data = await requests.ofertas.get("GetLentesAll", payload, auth.token);
        if (data?.status !== 200) return false;
        lentes = [...lentes, ...data.data];
      }
    }
    return lentes;
  }

  async function GetDiametros({ payload }) {
    const data = await requests.ofertas.get(
      "GetDiametros",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetDiametrosEspeciales({ payload }) {
    const data = await requests.ofertas.get(
      "GetDiametrosEspeciales",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetCaracteristicasLente({ payload }) {
    // Las características de una lente (VKORG+Cliente+Lente) son estáticas
    // por sesión. Dedupica requests con los mismos parámetros (típicamente
    // OD y OI en paralelo, o varios PDFs en batch del mismo cliente/lente).
    const key = JSON.stringify(payload ?? {});
    const cached = _caracteristicasLentePromises.get(key);
    if (cached) {
      const data = await cached;
      if (data) caracteristicas.value = data;
      return data;
    }
    const promise = (async () => {
      const data = await requests.ofertas.get(
        "GetCaracteristicasLente",
        payload,
        (await import("./auth")).useAuthStore().token,
      );
      if (data?.status !== 200) return false;
      caracteristicas.value = data.data;
      return data.data;
    })();
    _caracteristicasLentePromises.set(key, promise);
    promise.catch(() => { _caracteristicasLentePromises.delete(key); });
    return promise;
  }

  async function GetDatosAdicionales({ payload }) {
    const data = await requests.ofertas.get(
      "GetDatosAdicionales",
      payload,
      (await import("./auth")).useAuthStore().token,
    );
    if (data?.status !== 200) return false;
    return data.data;
  }

  async function GetTaladrosCliente({ cliente }) {
    try {
      const data = await requests.ofertas.getTaladrosCliente(
        cliente,
        (await import("./auth")).useAuthStore().token,
      );
      if (data?.status !== 200) {
        return [];
      }
      return data.data || [];
    } catch (error) {
      return [];
    }
  }

  async function GetSuplementos({ Jerga, Vkorg, Stock }) {
    try {
      const data = await requests.ofertas.getSuplementos(
        Jerga,
        Vkorg,
        Stock,
        (await import("./auth")).useAuthStore().token,
      );
      return data || [];
    } catch (error) {
      return [];
    }
  }

  function setTratamiento(data) {
    tratamiento.value = data;
  }
  function setIndice(data) {
    indice.value = data;
  }

  return {
    tratamiento,
    indice,
    caracteristicas,
    loadingInfo,
    setTratamiento,
    setIndice,
    LoadInfo,
    SaveInfo,
    GetFotocromaticos,
    GetColoraciones,
    GetColor,
    GetFocoyMaterial,
    GetIndices,
    GetTratamientos,
    GetTonos,
    GetLentes,
    GetLentesAll,
    GetDiametros,
    GetDiametrosEspeciales,
    GetCaracteristicasLente,
    GetDatosAdicionales,
    GetTaladrosCliente,
    GetSuplementos,
    // Escape hatches para invalidar la cache de SOAPs si en el futuro hace
    // falta forzar refresh manualmente (p. ej. cambios en el backend).
    _invalidateLoadInfoCache,
    _invalidateCaracteristicasLenteCache,
  };
});
